import {
  Box,
  Button,
  Heading,
  Text,
  useToast,
  Skeleton,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  useDisclosure,
  Center,
  ModalBody,
  ModalFooter,
  Divider,
  Flex,
  Avatar,
} from '@chakra-ui/react'
import InfoAlert from '../../../components/infoAlert/infoAlert'
import FormDrawer, {
  FormDataType,
} from '../../../components/formDrawer/formDrawer'
import { useEffect, useState } from 'react'
import {
  isCurrentUserAdmin,
  isCurrentUserEndUser,
  isCurrentUserStaff,
} from '../../../utils/helpers/basicFunc'
import {
  allDrawerInputs,
  mapKeyValue,
} from '../../../utils/pageDrawerInputLists/inputLists'
import { GET_BUILDING, GET_BUILDINGS } from '../../../graphql/API/queries'
import { useTranslation } from 'react-i18next'
import {
  AddCompanyRelationshipInput,
  Address,
  BuildingUpdateInput,
  BuildingWhereUniqueSearchOneInput,
  BuildingUploadConfigUpdateInput,
  Company,
  CompanyListQuery,
  CompanyRelationship,
  Exact,
  useAddCompanyRelationshipMutation,
  useBuildingDeleteMutation,
  useBuildingUpdateMutation,
  useCompanyListQuery,
  useDashboardUpdateMutation,
} from '../../../graphql/generated/graphql'
import BuildingRelationTable from '../buildingRelationTable/buildingRelationTable'
import { DragHandleIcon, SettingsIcon } from '@chakra-ui/icons'
import { useNavigate } from 'react-router-dom'
import {
  defaultUploadConfig,
  UploadConfigFormValues,
} from '../../../Pages/buildings/buildings'

export enum periodEnum {
  none = 'none',
  hourly = 'hourly',
  daily = 'daily',
  weekly = 'weekly',
  monthly = 'monthly',
  yearly = 'yearly',
}

export function isPeriodEnum(period: unknown): period is periodEnum {
  if (
    period === 'none' ||
    period === 'hourly' ||
    period === 'daily' ||
    period === 'weekly' ||
    period === 'monthly' ||
    period === 'yearly'
  ) {
    return true
  }
  return false
}

export enum StyleEnum {
  simple = 'simple',
  full = 'full',
  classic = 'classic',
  other = 'other',
}

export function isStyleEnum(style?: unknown): style is StyleEnum {
  if (
    style === 'simple' ||
    style === 'full' ||
    style === 'classic' ||
    style === 'graph' ||
    style === 'other'
  ) {
    return true
  }
  return false
}

type BuildingCompanyModalProps = {
  isOpen: boolean
  onClose: () => void
  companyRelationships: CompanyRelationship[]
  companyList: CompanyListQuery
  buildingId: string
  buildingSlug: string
}

const BuildingCompanyModal = ({
  isOpen,
  onClose,
  companyRelationships,
  companyList,
  buildingId,
  buildingSlug,
}: BuildingCompanyModalProps) => {
  const [addCompanyRelationship, { loading }] =
    useAddCompanyRelationshipMutation()
  const companySelectOptions = companyList?.companies.map(
    (company: Partial<Company>) => {
      return { value: company.id!, text: company.name! }
    },
  )
  const [isDrawerOpen, setDrawerVisibility] = useState(false)
  const [tableData, setTableData] = useState<CompanyRelationship[]>([])
  const toast = useToast()

  useEffect(() => {
    setTableData(companyRelationships)
  }, [companyRelationships])

  //NEED TO CHANGE THE SETSTATE LOGIC TO UPDATE METHOD PREFERRED BY APOLLO CLIENT

  const onDrawerFormSubmit = (data: FormDataType) => {
    const companyRelationInput = {
      ...data,
      buildingId,
    } as AddCompanyRelationshipInput
    addCompanyRelationship({
      variables: { data: companyRelationInput },
      refetchQueries: [
        { query: GET_BUILDING, variables: { where: { slug: buildingSlug } } },
      ],
      onCompleted: (res) => {
        const newTableData = [
          ...tableData,
          res.addCompanyRelationship,
        ] as CompanyRelationship[]
        setDrawerVisibility(false)
        setTableData(newTableData)
        toast({
          title: 'Relation created successfully',
          status: 'success',
          position: 'top',
          duration: 3000,
          isClosable: true,
        })
      },
      onError: (err) => {
        toast({
          title: err.message,
          status: 'error',
          position: 'top',
          duration: 3000,
          isClosable: true,
        })
      },
    })
  }

  const onDelete = (values: CompanyRelationship) => {
    const afterDelete = tableData?.filter(
      (data) => data.companyId !== values.companyId,
    )
    setTableData(afterDelete)
  }

  const onUpdate = (values: CompanyRelationship) => {
    const afterUpdate = tableData.map((data) =>
      data.companyId === values.companyId ? values : data,
    )
    setTableData(afterUpdate)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={'full'}>
      <ModalOverlay />
      <ModalContent bg={'white'}>
        <ModalHeader>
          <Center justifyContent={'space-between'}>
            <Text>Building-Company Relationship</Text>
            <Button
              onClick={() => setDrawerVisibility(true)}
              colorScheme={'purple'}
            >
              + Create relation
            </Button>
          </Center>
        </ModalHeader>
        <ModalBody>
          <BuildingRelationTable
            companyRelationships={tableData!}
            companySelectOptions={companySelectOptions}
            onRelationDelete={onDelete}
            onRelationUpdate={onUpdate}
            buildingSlug={buildingSlug}
          />
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
        <FormDrawer
          openedFrom={'Company Relation'}
          isCreate={true}
          isOpen={isDrawerOpen}
          onClose={() => setDrawerVisibility(false)}
          loading={loading}
          inputElements={allDrawerInputs['companyRelationCreate']}
          onDrawerFormSubmit={onDrawerFormSubmit}
          selectOptions={companySelectOptions}
        />
        {/* <ModalCloseButton /> */}
      </ModalContent>
    </Modal>
  )
}

type BuildingRowProps = {
  id: string
  name: string
  defaultName: string
  type: string
  picture: string
  floorArea: number
  epcRating: string
  isOccupied: boolean
  isSubscribedDatalake: boolean
  isSubscribedOptitwin: boolean
  companyRelationships: CompanyRelationship[]
  projectRef: string
  loading: boolean
  buildingSlug: string
  epcUrl: string
  address: Address
  constructionYear: number
  breeamScore: number
  uploadConfigs?: UploadConfigFormValues[]
  dashboardStyle?: string
  dashboardPeriod?: periodEnum
  tryMessage: string
  infoMessage: string
  refetchBuildingGetOne: (
    arg:
      | Partial<Exact<{ where: BuildingWhereUniqueSearchOneInput }>>
      | undefined,
  ) => {}
}

export default function BuildingConfiguration({
  id,
  name,
  defaultName,
  type,
  picture,
  floorArea,
  isOccupied,
  isSubscribedDatalake,
  isSubscribedOptitwin,
  companyRelationships,
  epcRating,
  projectRef,
  loading,
  buildingSlug,
  address,
  epcUrl,
  breeamScore,
  constructionYear,
  dashboardPeriod,
  dashboardStyle,
  tryMessage,
  infoMessage,
  refetchBuildingGetOne,
  uploadConfigs,
}: BuildingRowProps) {
  const { t } = useTranslation('building')
  const toast = useToast()
  const toastSuccess = (message: string) =>
    toast({
      title: message,
      status: 'success',
      position: 'top',
      duration: 3000,
      isClosable: true,
    })
  const toastFail = (message: string) =>
    toast({
      title: message,
      status: 'error',
      position: 'top',
      duration: 3000,
      isClosable: true,
    })

  // if (!periodGuard(dashboardPeriod)) {
  //   console.error(
  //     `DataQuality received unexpected dashboardPeriod value : ${dashboardPeriod}`,
  //   )
  // }

  const slug = buildingSlug ?? 'unknown_building'

  // State variables
  const [isDeleteOpen, setDeleteOpen] = useState(false)
  const [isBuildingEditOpen, setBuildingEditOpen] = useState(false)
  const [isDashboardEditOpen, setDashboardEditOpen] = useState(false)
  const [buildingUploadConfig, setBuildingUploadConfig] = useState<
    BuildingUploadConfigUpdateInput[]
  >([])
  const {
    isOpen: isRelationshipsOpen,
    onOpen: openRelationships,
    onClose: closeRelationships,
  } = useDisclosure()
  const navigate = useNavigate()

  // Company list
  const companyList = useCompanyListQuery({
    variables: { where: {} },
  }).data

  const dashboardEditProps = {
    page: 'dashboardEdit',
    data: {
      period: dashboardPeriod ?? '',
      dashboardStyle: dashboardStyle ?? '',
      tryMessage: tryMessage ?? '',
      infoMessage: infoMessage ?? '',
    },
  }

  const [updateDashboard, { loading: dashboardEditLoader }] =
    useDashboardUpdateMutation()

  useEffect(() => {
    if (uploadConfigs) {
      setBuildingUploadConfig(
        uploadConfigs as BuildingUploadConfigUpdateInput[],
      )
    }
  }, [uploadConfigs])

  const onDashboardEditSubmit = (formData: FormDataType) => {
    if (!isPeriodEnum(formData.period)) {
      throw Error(
        `dashboard update received unexpected period type : ${formData.period}`,
      )
    } else if (formData.period === 'none' || formData.period === 'hourly') {
      throw Error(
        `${formData.period} is an invalid period option for the dashboard`,
      )
    }
    if (!isStyleEnum(formData.dashboardStyle)) {
      throw Error(`${formData.dashboardStyle} is an invalid dashboard option`)
    }
    updateDashboard({
      variables: {
        data: {
          buildingId: id,
          period: formData.period,
          dashboardStyle: formData.dashboardStyle,
          tryMessage: formData.tryMessage as string,
          infoMessage: formData.infoMessage as string,
        },
      },
      refetchQueries: [
        {
          query: GET_BUILDING,
          variables: {
            where: { slug },
          },
        },
        {
          query: GET_BUILDINGS,
          variables: { where: {} },
        },
      ],
      onCompleted: (response) => {
        setDashboardEditOpen(false)
        toastSuccess('Dashboard updated successfully')
      },
      onError: (err) => {
        toastFail(err.message)
      },
    })
  }

  const uploadConfigCrud = (
    type: 'add' | 'edit' | 'delete',
    editedConfig?: UploadConfigFormValues[],
  ) => {
    if (type === 'add') {
      const addedConfigs = structuredClone(buildingUploadConfig)
      addedConfigs.push(defaultUploadConfig as BuildingUploadConfigUpdateInput)
      setBuildingUploadConfig(addedConfigs)
    } else if ((type === 'edit' || type === 'delete') && editedConfig) {
      setBuildingUploadConfig(editedConfig as BuildingUploadConfigUpdateInput[])
    }
  }

  const buildingEditProps = {
    page: 'buildingsEdit',
    data: {
      id,
      type, // What is this?
      name,
      defaultName,
      isOccupied,
      isSubscribedDatalake,
      isSubscribedOptitwin,
      floorArea,
      epcRating,
      epcUrl,
      slug,
      picture,
      projectRef,
      breeamScore,
      constructionYear,
      city: address?.city ?? '',
      country: address?.country ?? '',
      firstLine: address?.firstLine ?? '',
      postcode: address?.postcode ?? '',
      region: address?.region ?? '',
      gasRegion: address?.gasRegion ?? '',
      secondLine: address?.secondLine ?? '',
    },
  }
  const [updateBuilding, { loading: buildingEditLoader }] =
    useBuildingUpdateMutation()
  const onBuildingEditSubmit = (formData: FormDataType) => {
    updateBuilding({
      variables: {
        data: {
          id,
          name: formData?.name,
          defaultName: formData?.defaultName,
          company: formData?.company,
          epcRating: formData?.epcRating,
          epcUrl: formData?.epcUrl,
          picture: formData?.picture,
          slug: formData?.slug,
          isOccupied: formData?.isOccupied,
          isSubscribedDatalake: formData?.isSubscribedDatalake,
          isSubscribedOptitwin: formData?.isSubscribedOptitwin,
          floorArea: formData?.floorArea ? +formData.floorArea : null,
          thumb: formData?.picture,
          breeamScore: formData?.breeamScore ? +formData.breeamScore : null,
          constructionYear: formData?.constructionYear
            ? +formData.constructionYear
            : null,
          address: {
            firstLine: formData.firstLine ?? '',
            secondLine: formData.secondLine ?? '',
            city: formData.city ?? '',
            region: formData.region ?? '',
            country: formData.country ?? '',
            postcode: formData.postcode ?? '',
            gasRegion: formData.gasRegion ?? '',
          },
          uploadConfig: buildingUploadConfig,
        } as BuildingUpdateInput,
      },
      refetchQueries: [
        {
          query: GET_BUILDINGS,
          variables: { where: {} },
        },
      ],
      onCompleted: (response) => {
        setBuildingEditOpen(false)
        refetchBuildingGetOne({
          where: { slug: response.buildingUpdate.slug },
        })
        if (buildingSlug !== response.buildingUpdate.slug) {
          navigate(`/buildings/${response.buildingUpdate.slug}`)
        }
        toastSuccess('Building updated successfully')
      },
      onError: (err) => {
        toastFail(err.message)
      },
    })
  }

  const [deleteBuilding] = useBuildingDeleteMutation({
    refetchQueries: [
      {
        query: GET_BUILDINGS,
        variables: {
          where: {},
        },
      },
    ],
    onCompleted: (response) => {
      toast({
        title: t('deleteOkResult'),
        status: 'success',
        position: 'top',
        duration: 3000,
        isClosable: true,
      })
    },
    onError: (err) => {
      toast({
        title: err.message,
        status: 'error',
        position: 'top',
        duration: 3000,
        isClosable: true,
      })
    },
  })
  const onDelete = () => {
    deleteBuilding({ variables: { data: { id: id! } } })
    setDeleteOpen(false)
    window.location.href = `/buildings`
  }

  const hrefFunction = () => {
    window.location.href = `/buildings/${slug}/dashboard`
  }

  const InfoCard = (props: {
    // isLastElement: boolean
    companyRelationship: CompanyRelationship
  }) => {
    return (
      <Flex flexBasis={'50%'} mb={{ base: 4, xl: 3 }}>
        <Flex flex="1" gap="4" alignItems="center" flexWrap="nowrap">
          <Avatar
            bg={'gray.500'}
            icon={<DragHandleIcon fontSize={{ base: '1rem', xl: '1.5rem' }} />}
          />
          <Box>
            <Heading
              size={{ lg: 'md', xl: 'md' }}
              overflow={'hidden'}
              css={{
                '-webkit-line-clamp': '3',
                display: '-webkit-box',
                '-webkit-box-orient': 'vertical',
              }}
            >
              {props.companyRelationship.companyName}
            </Heading>
            <Text textTransform={'capitalize'}>
              {props.companyRelationship.relationshipType}
            </Text>
          </Box>
        </Flex>
      </Flex>
    )
  }

  const relationshipTypeSort = (
    a: CompanyRelationship,
    b: CompanyRelationship,
  ) => {
    const relationshipOrder: Record<string, number> = {
      owner: 0,
      occupier: 1,
      reseller: 2,
    }

    if (
      relationshipOrder[a.relationshipType] <
      relationshipOrder[b.relationshipType]
    ) {
      return -1
    }
    if (
      relationshipOrder[a.relationshipType] >
      relationshipOrder[b.relationshipType]
    ) {
      return 1
    }
    return 0
  }

  const reOrderedRelationships = (arr: CompanyRelationship[]) => {
    let mutatedArr = structuredClone(arr)
    mutatedArr.sort(relationshipTypeSort).slice(0, 2)
    return mutatedArr
  }

  return (
    <Box
      py={{ base: 2, md: 6 }}
      px={{ base: '0.5rem', md: 6 }}
      background={'gray.300'}
      textColor={'purple.600'}
      h="100%"
    >
      <Box
        background={'white'}
        p={4}
        h="100%"
        display="flex"
        flexDirection="column"
        justifyContent={isCurrentUserEndUser() ? 'flex-start' : 'space-between'}
      >
        <Heading as="h3" size="md" pb={6}>
          {!isCurrentUserEndUser() ? t('buildingConfig') : 'Building Details'}
        </Heading>
        <Skeleton isLoaded={!loading}>
          <Box>
            <Center
              flexDir={{ base: 'column', xl: 'row' }}
              alignItems={{ base: 'left', xl: 'center' }}
              justifyContent={'space-between'}
              flexWrap={'wrap'}
            >
              {reOrderedRelationships(companyRelationships).map(
                (relationshipData) => (
                  <InfoCard companyRelationship={relationshipData} />
                ),
              )}
            </Center>
          </Box>
          <Divider my={4} />
          <Stack direction="row" spacing={2} paddingTop={2}>
            <Button
              colorScheme={'purple.500'}
              variant={'outline'}
              width={'100%'}
              onClick={() => hrefFunction()}
              p={4}
            >
              {t('dashboard')}
            </Button>
            {isCurrentUserAdmin() && (
              <Button
                colorScheme={'purple.500'}
                leftIcon={<SettingsIcon />}
                iconSpacing="0"
                variant={'outline'}
                // width={'100%'}
                onClick={() => setDashboardEditOpen(true)}
                p={2}
                fontSize="2xl"
              />
            )}

            <FormDrawer
              openedFrom={'Dashboard'}
              isCreate={false}
              isOpen={isDashboardEditOpen}
              onClose={() => setDashboardEditOpen(false)}
              inputElements={mapKeyValue(dashboardEditProps)}
              onDrawerFormSubmit={onDashboardEditSubmit}
              loading={dashboardEditLoader}
            />
          </Stack>
          {isCurrentUserStaff() && (
            <Button
              colorScheme={'purple.500'}
              variant={'outline'}
              width={'100%'}
              onClick={openRelationships}
              p={4}
              mt={2}
            >
              {t('relationshipsEdit')}
            </Button>
          )}

          <BuildingCompanyModal
            isOpen={isRelationshipsOpen}
            onClose={closeRelationships}
            companyRelationships={companyRelationships}
            companyList={companyList!}
            buildingId={id!}
            buildingSlug={buildingSlug}
          />

          <Stack direction="row" spacing={2} paddingTop={2}>
            {isCurrentUserAdmin() && (
              <Button
                variant={'outline'}
                colorScheme={'purple.500'}
                width={'100%'}
                onClick={() => setBuildingEditOpen(true)}
                p={'4px'}
                fontSize={'15px'}
              >
                {t('editBuilding')}
              </Button>
            )}

            <FormDrawer
              openedFrom={'Building'}
              isCreate={false}
              isOpen={isBuildingEditOpen}
              onClose={() => setBuildingEditOpen(false)}
              inputElements={mapKeyValue(buildingEditProps)}
              onDrawerFormSubmit={onBuildingEditSubmit}
              loading={buildingEditLoader}
              modalInputs={buildingUploadConfig}
              modalCrudFunc={uploadConfigCrud}
            />
            {isCurrentUserStaff() && (
              <Button
                colorScheme={'red'}
                ml={4}
                width={'100%'}
                variant={'outline'}
                onClick={() => setDeleteOpen(true)}
                padding={'4px'}
                fontSize={'15px'}
              >
                {t('deleteBuilding')}
              </Button>
            )}

            <InfoAlert
              isOpen={isDeleteOpen}
              onClose={() => setDeleteOpen(false)}
              onSubmit={onDelete}
              headerText={`${t('deleteText')} ?`}
              bodyText={t('deleteConfirmMsg')}
              buttonText={t('deleteText')}
            />
          </Stack>
        </Skeleton>
      </Box>
    </Box>
  )
}
